import type { CamelCaseCarDetail } from '@/apis/server/new-car/v1/car-detail';
import {
  VEHICLE_VALUE_ALL_COMMERCIAL_VEHICLES,
  VEHICLE_VALUE_LUXURY_SEDAN,
  VEHICLE_VALUE_SPORTS_CARS,
} from '@/constants/global';

import {
  LINK_KEY_ACCESSORIES,
  LINK_KEY_PHOTOS,
  LINK_KEY_PRICING,
  LINK_KEY_PROMO,
  LINK_KEY_RESEARCH,
  LINK_KEY_REVIEWS,
  LINK_KEY_SIMILAR,
} from './nav-tab-header/constants';
import type { LinkKey } from './nav-tab-header/types';

export const isLinkKey = (value: unknown): value is LinkKey => (
  typeof value === 'undefined' || (typeof value === 'string' && [
    LINK_KEY_PRICING,
    LINK_KEY_PHOTOS,
    LINK_KEY_RESEARCH,
    LINK_KEY_REVIEWS,
    LINK_KEY_SIMILAR,
    LINK_KEY_ACCESSORIES,
    LINK_KEY_PROMO,
  ].includes(value))
);

export const linkKeyMap = (value: string): string => {
  switch (value) {
    case 'submit-review':
      return LINK_KEY_REVIEWS;
    default:
      return value;
  }
};

export const carDetailDataLayer: (
  carDetail: CamelCaseCarDetail
) => Record<string, string> = (carDetail) => {
  const result: Record<string, string> = {};

  result.make = carDetail.make;
  result.modelName = carDetail.model;
  result.vehicleType = carDetail.vehicleType;

  if (carDetail.subModels[0]?.specifications.fuelType) {
    const { fuelType } = carDetail.subModels[0].specifications;
    const formattedFuelType = fuelType
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('-');

    result.fuelType = formattedFuelType;
  } else {
    result.fuelType = '';
  }

  if (carDetail.subModels[0]?.price) {
    result.price = carDetail.subModels[0]?.price.toString();
  } else {
    result.price = '';
  }

  if (carDetail.subModels[0]?.depreciation) {
    result.depreciation = carDetail.subModels[0]?.depreciation?.toString();
  } else {
    result.depreciation = '';
  }

  return result;
};

export const carDetailTargetMapping: (
  carDetail: CamelCaseCarDetail
) => Record<string, string> = (carDetail) => {
  const result: Record<string, string> = {};

  result.pageType = 'new-cars-info';
  result.newcarMake = carDetail.make.toLowerCase().split(' ').join('-');

  if (carDetail.vehicleType.toLowerCase() === VEHICLE_VALUE_SPORTS_CARS) {
    result.newcarType = 'sports-car';
  } else if (carDetail.vehicleType
    .toLowerCase() === VEHICLE_VALUE_ALL_COMMERCIAL_VEHICLES) {
    result.newcarType = 'commercial-vehicles';
  } else if (carDetail.vehicleType
    .toLowerCase() === VEHICLE_VALUE_LUXURY_SEDAN) {
    result.newcarType = 'luxury-car';
  } else if (carDetail.vehicleType) {
    result.newcarType = carDetail.vehicleType.toLowerCase();
  }

  if (carDetail.subModels[0]?.specifications.fuelType) {
    const { fuelType } = carDetail.subModels[0].specifications;

    result.newcarFuelType = fuelType;
  }

  return result;
};
